// MARCA | 19/10/2026 | New file – Mirrors premium gift card messages (line item "Message" property) into the cart note and/or
// cart attributes so they reach Linnworks, which does not display line item properties. The line item property is kept as the
// source of truth; the note/attributes are re-derived from the cart after every cart change.
import { ThemeEvents } from '@theme/events';

/** @typedef {{ label: string, message: string }} GiftcardMessage */

// MARCA | 19/10/2026 | FIRST_GIFTCARD_MARKER is also used by cart-summary.liquid to split the customer's message out of the note – keep in sync
var GIFT_MESSAGE_HEADING = '[Gift Message]';
var FIRST_GIFTCARD_MARKER = '[Gift Card 1 ';
var GIFTCARD_ATTRIBUTE_PATTERN = /^Gift Card \d+ /;

/** @type {any} */
var cachedCart = null;
/** @type {Promise<void>} */
var queue = Promise.resolve();
var pendingTasks = 0;
/** @type {string | null} */
var pendingCustomerNote = null;
var resubmitting = false;

/**
 * Storage mode from theme settings: 'note' | 'attributes' | 'both', or '' when the feature is off.
 * @returns {string}
 */
function getStorageMode() {
  return (typeof Theme !== 'undefined' && Theme.giftcard && Theme.giftcard.message_storage) || '';
}

function usesNote() {
  var mode = getStorageMode();
  return mode === 'note' || mode === 'both';
}

function usesAttributes() {
  var mode = getStorageMode();
  return mode === 'attributes' || mode === 'both';
}

/**
 * Build the key that associates a message with its card, e.g. "Gift Card 1 (Birthday) for Salted Caramel Box".
 * @param {number} index
 * @param {string | null} cardTitle
 * @param {string} productTitle
 * @returns {string}
 */
function buildLabel(index, cardTitle, productTitle) {
  return 'Gift Card ' + index + (cardTitle ? ' (' + cardTitle + ')' : '') + ' for ' + productTitle;
}

/**
 * Collect gift card messages from the cart, numbered in the order they were added (cart.js lists newest first).
 * @param {any} cart
 * @returns {GiftcardMessage[]}
 */
function getGiftcardMessages(cart) {
  var items = cart && cart.items ? cart.items.slice().reverse() : [];
  /** @type {GiftcardMessage[]} */
  var giftcards = [];

  for (var i = 0; i < items.length; i++) {
    var props = items[i].properties || {};
    var productTitle = props['Gift card for'];
    var message = props['Message'];
    if (!productTitle || !message) continue;

    giftcards.push({
      label: buildLabel(giftcards.length + 1, items[i].variant_title, productTitle),
      message: String(message).trim(),
    });
  }

  return giftcards;
}

/**
 * Return the customer's own gift message (from the cart drawer note input) without any gift card sections.
 * @param {string | null | undefined} note
 * @returns {string}
 */
export function extractCustomerNote(note) {
  var value = note || '';
  var markerIndex = value.indexOf(FIRST_GIFTCARD_MARKER);
  if (markerIndex === -1) return value;

  var customerNote = value.slice(0, markerIndex).trim();
  if (customerNote.indexOf(GIFT_MESSAGE_HEADING) === 0) {
    customerNote = customerNote.slice(GIFT_MESSAGE_HEADING.length).trim();
  }
  return customerNote;
}

/**
 * Compose the cart note. With no gift cards the customer's note is returned untouched (no heading added).
 * @param {string} customerNote
 * @param {GiftcardMessage[]} giftcards
 * @returns {string}
 */
function buildNote(customerNote, giftcards) {
  if (giftcards.length === 0) return customerNote;

  var sections = giftcards.map(function (giftcard) {
    return '[' + giftcard.label + ']\n' + giftcard.message;
  });
  var trimmedCustomerNote = customerNote.trim();
  if (trimmedCustomerNote) sections.unshift(GIFT_MESSAGE_HEADING + '\n' + trimmedCustomerNote);

  return sections.join('\n\n');
}

/**
 * Diff current cart attributes against the desired gift card attributes. Stale gift card keys are cleared with ''.
 * @param {Record<string, string> | undefined} currentAttributes
 * @param {GiftcardMessage[]} giftcards
 * @returns {Record<string, string>}
 */
function buildAttributeUpdates(currentAttributes, giftcards) {
  var current = currentAttributes || {};
  /** @type {Record<string, string>} */
  var desired = {};
  /** @type {Record<string, string>} */
  var updates = {};

  giftcards.forEach(function (giftcard) {
    desired[giftcard.label] = giftcard.message;
  });

  Object.keys(current).forEach(function (key) {
    if (GIFTCARD_ATTRIBUTE_PATTERN.test(key) && !(key in desired) && current[key] !== '') updates[key] = '';
  });

  Object.keys(desired).forEach(function (key) {
    if (current[key] !== desired[key]) updates[key] = desired[key];
  });

  return updates;
}

/**
 * Build the /cart/update.js payload needed to bring the cart in line with the current storage mode.
 * Disabled channels are still diffed (against no gift cards) so switching modes cleans up the old location.
 * @param {any} cart
 * @param {string} customerNote
 * @returns {{ note?: string, attributes?: Record<string, string> } | null}
 */
function buildPayload(cart, customerNote) {
  var giftcards = getGiftcardMessages(cart);
  /** @type {{ note?: string, attributes?: Record<string, string> }} */
  var payload = {};

  var nextNote = buildNote(customerNote, usesNote() ? giftcards : []);
  if (nextNote !== (cart.note || '')) payload.note = nextNote;

  var attributeUpdates = buildAttributeUpdates(cart.attributes, usesAttributes() ? giftcards : []);
  if (Object.keys(attributeUpdates).length > 0) payload.attributes = attributeUpdates;

  return payload.note !== undefined || payload.attributes ? payload : null;
}

/**
 * Serialise all note/attribute writes so concurrent cart events and typing can't overwrite each other.
 * @param {() => Promise<void>} task
 * @returns {Promise<void>}
 */
function enqueue(task) {
  pendingTasks += 1;
  var run = queue
    .then(task)
    .catch(function (err) {
      console.error('[GiftcardNote] Sync failed:', err);
    })
    .finally(function () {
      pendingTasks -= 1;
    });
  queue = run;
  return run;
}

async function fetchCart() {
  var res = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
  cachedCart = await res.json();
  return cachedCart;
}

/**
 * @param {any} cart
 * @param {string} customerNote
 */
async function writeCart(cart, customerNote) {
  var payload = buildPayload(cart, customerNote);
  if (!payload) return;

  var res = await fetch(Theme.routes.cart_update_url + '.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Cart update failed: ' + res.status);
  cachedCart = await res.json();
}

/**
 * Re-derive the note/attributes from a fresh cart. Runs after every cart change.
 * @returns {Promise<void>}
 */
function syncGiftcardMessages() {
  return enqueue(async function () {
    var cart = await fetchCart();
    var customerNote = pendingCustomerNote !== null ? pendingCustomerNote : extractCustomerNote(cart.note);
    pendingCustomerNote = null;
    await writeCart(cart, customerNote);
  });
}

/**
 * Called by cart-note.js when the customer edits their gift message. Rapid edits are coalesced into one write.
 * @param {string} customerNote
 * @returns {Promise<void>}
 */
export function updateCustomerNote(customerNote) {
  pendingCustomerNote = customerNote;
  return enqueue(async function () {
    if (pendingCustomerNote === null) return;
    var latestNote = pendingCustomerNote;
    pendingCustomerNote = null;
    var cart = cachedCart || (await fetchCart());
    await writeCart(cart, latestNote);
  });
}

// MARCA | 19/10/2026 | Re-sync after every cart change (PDP batch add, nested remove, bundle remove, orphan cleanup, quantity changes)
document.addEventListener(ThemeEvents.cartUpdate, function () {
  if (!getStorageMode()) return;
  cachedCart = null;
  syncGiftcardMessages();
});

// MARCA | 19/10/2026 | One-time page-load sync – catches carts built before this feature or a failed sync. Only runs when the cart has items.
function initialSync() {
  if (!getStorageMode()) return;
  if (!document.querySelector('[data-giftcard-cleanup-product-id]')) return;
  syncGiftcardMessages();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialSync);
} else {
  initialSync();
}

// MARCA | 19/10/2026 | Checkout posts the cart form, which includes the note. Write the full composed note into the hidden
// note input first (waiting for any in-flight sync) so the gift card sections aren't replaced by the customer's message alone.
document.addEventListener(
  'submit',
  function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || resubmitting || !usesNote()) return;

    var elements = Array.from(form.elements);
    var noteInput = /** @type {HTMLInputElement | undefined} */ (
      elements.find(function (el) {
        return el instanceof HTMLInputElement && el.hasAttribute('data-giftcard-note-input');
      })
    );
    if (!noteInput) return;

    var textarea = /** @type {HTMLTextAreaElement | undefined} */ (
      elements.find(function (el) {
        return el instanceof HTMLTextAreaElement && !!el.closest('cart-note');
      })
    );
    var customerNote = textarea ? textarea.value : extractCustomerNote(noteInput.value);
    var input = noteInput;
    var cartForm = form;

    function applyNote() {
      if (cachedCart) input.value = buildNote(customerNote, getGiftcardMessages(cachedCart));
    }

    if (cachedCart && pendingTasks === 0) {
      applyNote();
      return;
    }

    e.preventDefault();
    var submitter = /** @type {SubmitEvent} */ (e).submitter;

    updateCustomerNote(customerNote).finally(function () {
      applyNote();
      resubmitting = true;
      try {
        cartForm.requestSubmit(submitter instanceof HTMLElement ? submitter : undefined);
      } finally {
        resubmitting = false;
      }
    });
  },
  true
);
